import React from 'react';
import Icon from '../../../components/AppIcon';

const SessionAnalytics = ({ 
  analytics = {},
  timeRange = 'today'
}) => {
  const defaultAnalytics = {
    totalSessions: 24,
    activeSessions: 8,
    completedSessions: 12,
    averageSessionTime: 45,
    averageParticipants: 4.2,
    totalRevenue: 1847.50,
    completionRate: 85,
    averageResolutionTime: 38,
    disputeRate: 3.2,
    peakHours: '20:00-21:00'
  };

  const data = { ...defaultAnalytics, ...analytics };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    })?.format(amount);
  };

  const formatPercentage = (value) => {
    return `${value?.toFixed(1)}%`;
  };

  const getTimeRangeLabel = () => {
    switch (timeRange) {
      case 'today':
        return 'Hoy';
      case 'yesterday':
        return 'Ayer';
      case 'week':
        return 'Esta Semana';
      case 'month':
        return 'Este Mes';
      default:
        return 'Hoy';
    }
  };

  const metrics = [
    {
      id: 'total-sessions',
      label: 'Total de Sesiones',
      value: data?.totalSessions,
      icon: 'BarChart3',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      id: 'active-sessions',
      label: 'Sesiones Activas',
      value: data?.activeSessions,
      icon: 'Activity',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    {
      id: 'completion-rate',
      label: 'Tasa de Finalización',
      value: formatPercentage(data?.completionRate),
      icon: 'CheckCircle',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    {
      id: 'total-revenue',
      label: 'Ingresos Totales',
      value: formatAmount(data?.totalRevenue),
      icon: 'DollarSign',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      id: 'avg-session-time',
      label: 'Tiempo Promedio',
      value: `${data?.averageSessionTime}m`,
      icon: 'Clock',
      color: 'text-warning',
      bgColor: 'bg-warning/10'
    },
    {
      id: 'avg-participants',
      label: 'Participantes Promedio',
      value: data?.averageParticipants?.toFixed(1),
      icon: 'Users',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      id: 'resolution-time',
      label: 'Tiempo de Resolución',
      value: `${data?.averageResolutionTime}m`,
      icon: 'Timer',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    {
      id: 'dispute-rate',
      label: 'Tasa de Disputas',
      value: formatPercentage(data?.disputeRate),
      icon: 'AlertTriangle',
      color: 'text-error',
      bgColor: 'bg-error/10'
    }
  ];

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">
            Análisis de Sesiones
          </h3>
          <span className="text-sm text-text-secondary">
            {getTimeRangeLabel()}
          </span>
        </div>
      </div>
      {/* Metrics Grid */}
      <div className="p-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {metrics?.map((metric) => (
            <div
              key={metric?.id}
              className="p-4 border border-border rounded-lg hover:shadow-elevation-1 transition-shadow"
            >
              <div className="flex items-center justify-between mb-2">
                <div className={`w-10 h-10 rounded-lg ${metric?.bgColor} flex items-center justify-center`}>
                  <Icon name={metric?.icon} size={20} className={metric?.color} />
                </div>
              </div>
              
              <div className="space-y-1">
                <div className="text-2xl font-bold text-foreground font-mono">
                  {metric?.value}
                </div>
                <div className="text-sm text-text-secondary">
                  {metric?.label}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Insights */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Peak Hours */}
          <div className="p-4 border border-border rounded-lg">
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="TrendingUp" size={16} className="text-primary" />
              <h4 className="font-medium text-foreground">Horas Pico</h4>
            </div>
            <div className="text-2xl font-mono font-semibold text-foreground mb-1">
              {data?.peakHours}
            </div>
            <div className="text-sm text-text-secondary">
              Mayor actividad de sesiones
            </div>
          </div>

          {/* Session Status Distribution */}
          <div className="p-4 border border-border rounded-lg">
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="PieChart" size={16} className="text-primary" />
              <h4 className="font-medium text-foreground">Distribución de Estados</h4>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Activas</span>
                <span className="text-sm font-medium text-success">{data?.activeSessions}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Completadas</span>
                <span className="text-sm font-medium text-primary">{data?.completedSessions}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Pendientes</span>
                <span className="text-sm font-medium text-warning">
                  {data?.totalSessions - data?.activeSessions - data?.completedSessions}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Indicators */}
        <div className="mt-6 p-4 bg-muted/30 rounded-lg">
          <h4 className="font-medium text-foreground mb-3">Indicadores de Rendimiento</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-lg font-semibold text-success">Excelente</div>
              <div className="text-sm text-text-secondary">Tasa de Finalización</div>
              <div className="w-full bg-border rounded-full h-2 mt-1">
                <div 
                  className="bg-success h-2 rounded-full transition-all duration-300"
                  style={{ width: `${data?.completionRate}%` }}
                />
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-lg font-semibold text-warning">Bueno</div>
              <div className="text-sm text-text-secondary">Tiempo de Resolución</div>
              <div className="w-full bg-border rounded-full h-2 mt-1">
                <div 
                  className="bg-warning h-2 rounded-full transition-all duration-300"
                  style={{ width: '75%' }}
                />
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-lg font-semibold text-success">Excelente</div>
              <div className="text-sm text-text-secondary">Tasa de Disputas</div>
              <div className="w-full bg-border rounded-full h-2 mt-1">
                <div 
                  className="bg-success h-2 rounded-full transition-all duration-300"
                  style={{ width: `${100 - (data?.disputeRate * 10)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionAnalytics;